import { useState, useRef, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { getCategoryIcon } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ShieldCheck, Zap, MapPin, Percent, UploadCloud, X, Loader2, ArrowRight,
  HardHat, PhoneCall, Phone, ClipboardList, Handshake, Star, Menu,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { BretonFlag } from "@/components/BretonFlag";

const HERO_IMG =
  "https://images.unsplash.com/photo-1731168273756-e02cae42265b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHszfHxob21lJTIwcmVub3ZhdGlvbiUyMGNyYWZ0c21hbnNoaXAlMjBjYXJwZW50ZXIlMjBwbHVtYmVyJTIwZWxlY3RyaWNpYW58ZW58MHx8fHwxNzg5MjE1Nzc0fDA&ixlib=rb-4.1.0&q=85";

const TRUST = [
  { icon: ShieldCheck, label: "100% Artisans Vérifiés" },
  { icon: Zap, label: "Devis Gratuit & Rapide" },
  { icon: MapPin, label: "100% Bretagne" },
  { icon: Percent, label: "Sans engagement" },
];

const STEPS = [
  { icon: ClipboardList, title: "Décrivez vos travaux", text: "Remplissez le formulaire en 2 minutes, c'est gratuit et sans engagement." },
  { icon: HardHat, title: "On trouve l'artisan", text: "Notre équipe sélectionne un professionnel vérifié près de chez vous." },
  { icon: Handshake, title: "Réalisez votre projet", text: "L'artisan vous contacte, établit un devis et démarre le chantier." },
];

export default function Landing() {
  const navigate = useNavigate();
  const fileInput = useRef(null);
  const [form, setForm] = useState({ category: "", city: "", description: "", full_name: "", phone: "", email: "" });
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [phone, setPhone] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [consent, setConsent] = useState(false);

  useEffect(() => {
    api.get("/public/categories").then((res) => setAvailableCategories(res.data.categories || [])).catch(() => setAvailableCategories([]));    api.get("/public/company").then((res) => setPhone(res.data.phone || "")).catch(() => {});
  }, []);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleFiles = async (files) => {
    const list = Array.from(files).slice(0, 6);
    setUploading(true);
    for (const file of list) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPhotos((p) => [...p, { ...data, preview: URL.createObjectURL(file) }]);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
      }
    }
    setUploading(false);
  };

  const removePhoto = (path) => setPhotos((p) => p.filter((x) => x.path !== path));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.category || !form.city || !form.description || !form.full_name || !form.phone || !form.email) {
      toast.error("Merci de remplir tous les champs obligatoires.");
      return;
    }
    if (!consent) {
      toast.error("Veuillez accepter l'utilisation de vos données pour continuer.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/requests", {
        ...form,
        photos: photos.map((p) => ({ path: p.path, filename: p.filename })),
      });
      navigate("/confirmation", { state: { reference: data.reference, name: form.full_name } });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 overflow-x-hidden" data-testid="landing-page">
      {/* Bande Gwenn ha Du (drapeau breton) */}
      <div className="gwenn-ha-du h-2.5 w-full relative z-40" data-testid="breton-band-top" />
      {/* Nav */}
      <header className="absolute top-2.5 inset-x-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 h-20 sm:h-24 flex items-center justify-between gap-2">
          <Logo flagClass="h-8 sm:h-11" textClass="text-lg sm:text-2xl" boxed />
          <div className="flex items-center gap-2 sm:gap-4 shrink-0">
            {phone && phone.trim() && (
              <a
                href={`tel:${phone.replace(/\s/g, "")}`}
                data-testid="call-button"
                aria-label="Appelez-nous"
                className="inline-flex items-center gap-2 rounded-full bg-blue-600 hover:bg-blue-700 text-white px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold whitespace-nowrap transition-colors shadow-md"
              >
                <Phone className="h-4 w-4" />
                <span>Appelez-nous</span>
              </a>
            )}
            <button
              onClick={() => navigate("/suivi")}
              data-testid="nav-track-link"
              className="hidden md:inline text-sm font-medium text-slate-200 hover:text-white transition-colors whitespace-nowrap"
            >
              Suivre ma demande
            </button>
            <button
              onClick={() => navigate("/admin/login")}
              data-testid="nav-admin-link"
              className="hidden md:inline text-sm font-medium text-slate-200 hover:text-white transition-colors whitespace-nowrap"
            >
              Espace Pro →
            </button>
            {/* Hamburger (mobile) */}
            <button
              onClick={() => setMenuOpen((o) => !o)}
              data-testid="mobile-menu-toggle"
              aria-label="Menu"
              aria-expanded={menuOpen}
              className="md:hidden inline-grid place-items-center h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
            >
              {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Mobile menu panel */}
        {menuOpen && (
          <div className="md:hidden px-4 mt-2" data-testid="mobile-menu">
            <div className="max-w-7xl mx-auto rounded-2xl bg-[#0B1E4D]/95 backdrop-blur ring-1 ring-white/15 shadow-xl overflow-hidden">
              <button
                onClick={() => { setMenuOpen(false); navigate("/suivi"); }}
                data-testid="mobile-nav-track-link"
                className="w-full text-left px-5 py-4 text-sm font-medium text-slate-100 hover:bg-white/10 transition-colors border-b border-white/10 flex items-center gap-3"
              >
                <ClipboardList className="h-4 w-4 text-blue-300" /> Suivre ma demande
              </button>
              <button
                onClick={() => { setMenuOpen(false); navigate("/admin/login"); }}
                data-testid="mobile-nav-admin-link"
                className="w-full text-left px-5 py-4 text-sm font-medium text-slate-100 hover:bg-white/10 transition-colors flex items-center gap-3"
              >
                <ShieldCheck className="h-4 w-4 text-blue-300" /> Espace Pro →
              </button>
            </div>
          </div>
        )}
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden bg-[#0B1E4D]">
        <img src={HERO_IMG} alt="Artisan au travail" className="absolute inset-0 h-full w-full object-cover opacity-30" />
        <div className="absolute inset-0 hero-grid" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B1E4D] via-[#0B1E4D]/90 to-[#0B1E4D]/40" />
        <BretonFlag className="pointer-events-none absolute right-6 bottom-6 h-[220px] opacity-[0.07] hidden lg:block" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 pt-28 pb-20 lg:pt-40 lg:pb-28 grid lg:grid-cols-2 gap-12 items-start">
          <div className="min-w-0">
            <span className="inline-flex items-center gap-2 rounded-full bg-blue-600/15 border border-blue-500/30 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-blue-300">
              <Star className="h-3.5 w-3.5" /> Réseau d'artisans certifiés
            </span>
            <h1 className="font-display mt-6 text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-white break-words">
              Vous avez des travaux ?<br />
              <span className="text-blue-400">Nous trouvons</span> le professionnel adapté.
            </h1>
            <p className="mt-6 text-lg text-slate-300 leading-relaxed max-w-full sm:max-w-xl">
              Mise en relation gratuite et sans engagement avec des artisans qualifiés et vérifiés près de chez vous. Décrivez votre projet, on s'occupe du reste.
            </p>
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-full sm:max-w-xl">
              {TRUST.map((t) => (
                <div key={t.label} className="flex items-center gap-2 rounded-xl bg-white/5 border border-white/10 px-3 py-2.5 min-w-0">
                  <t.icon className="h-4 w-4 text-blue-400 shrink-0" />
                  <span className="text-xs font-medium text-slate-200 leading-tight min-w-0">{t.label}</span>
                </div>
              ))}
            </div>
            <a href="#formulaire" className="mt-8 inline-flex items-center gap-2 text-blue-300 font-semibold hover:text-blue-200 transition-colors">
              Déposer ma demande <ArrowRight className="h-4 w-4" />
            </a>
          </div>

          {/* Form card */}
          <div id="formulaire" className="bg-white rounded-2xl shadow-2xl border border-slate-200 p-6 sm:p-8 min-w-0" data-testid="work-request-form">
            <h2 className="font-display text-xl font-bold text-slate-900">Décrivez votre projet</h2>
            <p className="text-sm text-slate-500 mt-1">Réponse sous 24h · 100% gratuit</p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <Label className="text-slate-700 font-medium">Catégorie de travaux *</Label>
                <Select value={form.category} onValueChange={(v) => setField("category", v)}>
                  <SelectTrigger className="mt-1.5" data-testid="form-category-select">
                    <SelectValue placeholder="Sélectionnez votre type de projet..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableCategories.map((c) => {
                      const Icon = getCategoryIcon(c.icon);
                      return (
                        <SelectItem key={c.name} value={c.name} data-testid={`category-option-${c.name}`}>
                          <span className="flex items-center gap-2"><Icon className="h-4 w-4 text-blue-600" />{c.name}</span>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-slate-700 font-medium">Ville ou code postal *</Label>
                <Input className="mt-1.5" placeholder="Ex: Paris 11e, Lyon, Marseille..." value={form.city} onChange={(e) => setField("city", e.target.value)} data-testid="form-city-input" />
              </div>

              <div>
                <Label className="text-slate-700 font-medium">Description détaillée de votre projet *</Label>
                <Textarea className="mt-1.5 min-h-[96px]" placeholder="Expliquez vos besoins, la surface estimée, les contraintes particulières..." value={form.description} onChange={(e) => setField("description", e.target.value)} data-testid="form-description-input" />
              </div>

              <div>
                <Label className="text-slate-700 font-medium">Photos du chantier (optionnel)</Label>
                <div
                  onClick={() => fileInput.current?.click()}
                  className="mt-1.5 cursor-pointer rounded-xl border-2 border-dashed border-slate-300 hover:border-blue-400 hover:bg-blue-50/50 transition-colors p-5 text-center"
                  data-testid="form-photo-upload"
                >
                  {uploading ? (
                    <Loader2 className="h-6 w-6 mx-auto animate-spin text-blue-600" />
                  ) : (
                    <UploadCloud className="h-6 w-6 mx-auto text-slate-400" />
                  )}
                  <p className="text-sm text-slate-500 mt-2">Cliquez pour ajouter des photos</p>
                  <input ref={fileInput} type="file" accept="image/*" multiple className="hidden" onChange={(e) => handleFiles(e.target.files)} />
                </div>
                {photos.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {photos.map((p) => (
                      <div key={p.path} className="relative h-16 w-16 rounded-lg overflow-hidden border border-slate-200">
                        <img src={p.preview} alt={p.filename} className="h-full w-full object-cover" />
                        <button type="button" onClick={() => removePhoto(p.path)} className="absolute top-0.5 right-0.5 h-5 w-5 rounded-full bg-[#0B1E4D]/70 grid place-items-center text-white">
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-700 font-medium">Nom & Prénom *</Label>
                  <Input className="mt-1.5" placeholder="Jean Dupont" value={form.full_name} onChange={(e) => setField("full_name", e.target.value)} data-testid="form-fullname-input" />
                </div>
                <div>
                  <Label className="text-slate-700 font-medium">Téléphone *</Label>
                  <Input className="mt-1.5" placeholder="06 12 34 56 78" value={form.phone} onChange={(e) => setField("phone", e.target.value)} data-testid="form-phone-input" />
                </div>
              </div>

              <div>
                <Label className="text-slate-700 font-medium">Adresse email *</Label>
                <Input className="mt-1.5" type="email" placeholder="jean.dupont@example.com" value={form.email} onChange={(e) => setField("email", e.target.value)} data-testid="form-email-input" />
              </div>

              <div className="flex items-start gap-2.5" data-testid="consent-row">
                <Checkbox id="consent" checked={consent} onCheckedChange={(v) => setConsent(v === true)} className="mt-0.5 border-slate-400 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600" data-testid="form-consent-checkbox" />
                <Label htmlFor="consent" className="text-xs text-slate-500 leading-relaxed font-normal cursor-pointer">
                  J'accepte que mes données soient utilisées pour traiter ma demande.
                </Label>
              </div>

              <Button type="submit" disabled={submitting || !consent} className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-base font-semibold disabled:opacity-60" data-testid="form-submit-button">
                {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <><PhoneCall className="h-4 w-4 mr-2" /> Envoyer ma demande gratuitement</>}
              </Button>
            </form>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">Comment ça marche ?</h2>
        <p className="mt-3 text-slate-600 max-w-2xl">Un processus simple et transparent, du premier contact à la réalisation de votre chantier.</p>
        <div className="mt-10 grid md:grid-cols-3 gap-6">
          {STEPS.map((s, i) => (
            <div key={s.title} className="relative bg-white rounded-2xl border border-slate-200 p-7 shadow-sm hover:shadow-md transition-shadow">
              <span className="absolute top-6 right-6 font-display text-5xl font-extrabold text-slate-100">{i + 1}</span>
              <div className="h-12 w-12 rounded-xl bg-blue-600/10 grid place-items-center"><s.icon className="h-6 w-6 text-blue-600" /></div>
              <h3 className="font-display mt-5 text-lg font-semibold text-slate-900">{s.title}</h3>
              <p className="mt-2 text-slate-600 leading-relaxed">{s.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0B1E4D] text-slate-300">
        <div className="gwenn-ha-du h-2.5 w-full" data-testid="breton-band-footer" />
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12 flex flex-col sm:flex-row items-center justify-between gap-6">
          <Logo variant="full" flagClass="h-20 sm:h-24" boxed />
          <div className="flex flex-col items-center sm:items-end gap-2">
            <p className="text-sm text-center sm:text-right">Mise en relation particuliers & artisans professionnels · © {new Date().getFullYear()}</p>
            <Link to="/mentions-legales" data-testid="footer-legal-link" className="text-xs text-slate-300/80 hover:text-white underline underline-offset-2 transition-colors">
              Mentions légales
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
